import { useState } from 'react';

import { CopyToClipboard } from 'react-copy-to-clipboard';

import { styled } from '@mui/material/styles';
import MegaphoneFill from '@o2/icons/MegaphoneFill';
import { Button } from '@o2/ui';
import Box from '@o2/ui/Box';
import Link from '@o2/ui/Link';
import TextField from '@o2/ui/TextField';

import Typography from '@o2/ui/Typography';

import { useBreakpoint } from 'hooks/useBreakpoint';
import { useTranslationWithDataKey } from 'hooks/useTranslationWithDataKey';
import { KahlMessage as Message } from 'models/KahlMessage';
import FormattedHTML from 'views/components/FormattedHTML';
import Modal from 'views/components/Modal/Modal';

const DetailLink = styled(Link)(({ theme }) => ({
  color: theme.palette.common.white,
  textDecoration: 'none',
  cursor: 'pointer',
}));

const StacktraceField = styled(TextField)(({ theme }) => ({
  width: '100%',

  '.MuiOutlinedInput-root': {
    background: theme.palette.common.white,
    padding: 0,
  },
}));

interface Props {
  message: Message;
  funnelUrl: string;
  isInternal: boolean;
}

const KahlAlert = ({ message, funnelUrl, isInternal }: Props) => {
  const smDown = useBreakpoint('down', 'sm');
  const [showDetail, setShowDetail] = useState(false);
  const { t } = useTranslationWithDataKey();

  const handleDetailClick = () => {
    setShowDetail((value) => !value);
  };

  const formatStackTrace = (): string => {
    return (
      `${new Date()}\n${navigator.userAgent}\n${window.location.href}\n\n${message?.stacktrace
        ?.join()
        ?.split(',')
        ?.join('\n')}` || ''
    );
  };

  return (
    <>
      <Box mb={message.stacktrace && isInternal ? 2 : 0}>
        <FormattedHTML key={message.id} html={message?.text} />
      </Box>
      {message.stacktrace && isInternal && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box mb={showDetail ? 1 : 0} display="flex" flexDirection="column" gap={1}>
            {message.internal && message.textInternal}
            <DetailLink onClick={handleDetailClick}>{t('onecrm.general.KAHL.text.linkErrorDetail')}</DetailLink>
          </Box>
          {showDetail && (
            <Modal open={showDetail} onClose={() => setShowDetail(false)}>
              <Box>
                <Box p={2}>
                  <Typography variant="h2" align="center">
                    {t('onecrm.general.errorDetail.label.heading')}
                  </Typography>
                </Box>
                <Box p={smDown ? 2 : 4} sx={{ backgroundColor: '#edeff6' }}>
                  <StacktraceField
                    data-elm="2.46.5_002"
                    value={formatStackTrace()}
                    multiline
                    maxRows={12}
                    inputProps={{
                      readOnly: true,
                      sx: {
                        cursor: 'pointer',
                      },
                    }}
                  />
                  <Box pt={4}>
                    <Box display="flex" justifyContent="space-between">
                      <CopyToClipboard text={formatStackTrace()}>
                        <Button color="primary" variant="contained" data-elm="2.46.5_003">
                          {t('onecrm.general.errorDetail.text.buttonCopyError')}
                        </Button>
                      </CopyToClipboard>
                      <Button
                        color="primary"
                        variant="text"
                        data-elm="2.46.5_004"
                        startIcon={<MegaphoneFill />}
                        href={funnelUrl}
                      >
                        {t('onecrm.general.errorDetail.text.buttonReportError')}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Modal>
          )}
        </Box>
      )}
    </>
  );
};

export default KahlAlert;
